import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Modal,
  Paragraph,
  Select,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { LoadingParagraph } from '@zg-rentals/loading-components';
import { IconPlusOutline } from '@zillow/constellation-icons';
import { validate } from 'email-validator';
import type {
  InviteGuarantorWebApiResponseDto,
  ListingGuarantorPolicyWebApiDto,
  SelectedGuarantorCompanyWebApiDto,
  ThirdPartyGuarantorCompany,
} from '@zg-rentals/ts-capps-client';
import {
  CappsRenterGuarantorInvitationsWebApiHandler_getGuarantorInvite_queryOptions,
  useCappsRenterGuarantorCompanyWebApiHandler_saveGuarantorCompany,
  useCappsRenterGuarantorInvitationsWebApiHandler_inviteGuarantor,
  useCappsRenterGuarantorInvitationsWebApiHandler_removeGuarantor,
} from '@zg-rentals/ts-capps-client-react-query';
import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useApplicationContext } from '../../../../contexts/ApplicationContext';
import type { CurrentIncome } from '../../../../models/rental-application';
import { getNumberFromString } from '../../../../utils/formatters';
import { useWatch } from 'react-hook-form';
import { GUARANTOR_INVITE_TAG, useGuarantorInvite } from '../../../../hooks/useGuarantorInvite';
import { GuarantorSummary } from '../../../Guarantor/GuarantorSummary';
import { GuarantorCompanySummary } from '../../../Guarantor/GuarantorCompanySummary';
import { APPLICATIONS_FAQ_URL } from '../../../../constants/urls';

enum GuarantorType {
  PERSONAL = 'personal',
  COMPANY = 'company',
}

function useSaveGuarantorCompany(applicationId: string, details: string) {
  const queryClient = useQueryClient();
  const saveGuarantorCompany = useCappsRenterGuarantorCompanyWebApiHandler_saveGuarantorCompany({
    meta: {
      invalidates: [GUARANTOR_INVITE_TAG],
      context: { api: 'cappsRenterGuarantorCompany', details },
    },
  });

  async function save(selectedGuarantorCompany: SelectedGuarantorCompanyWebApiDto | undefined) {
    await saveGuarantorCompany.mutateAsync({
      postData: { applicationId, guarantorCompany: selectedGuarantorCompany?.company },
    });
    queryClient.setQueryData(
      CappsRenterGuarantorInvitationsWebApiHandler_getGuarantorInvite_queryOptions({ applicationId }).queryKey,
      (previous) => (previous ? { ...previous, selectedGuarantorCompany } : previous),
    );
  }

  return { save, isPending: saveGuarantorCompany.isPending };
}

export function isIncomeBelowRequirement(
  currentIncomes: Array<CurrentIncome | null> | null | undefined,
  monthlyPayment: string | null | undefined,
) {
  const monthlyRent = getNumberFromString(monthlyPayment);
  if (monthlyRent <= 0) {
    return false;
  }

  const monthlyIncome = (currentIncomes ?? []).reduce(
    (total, income) => total + getNumberFromString(income?.amount),
    0,
  );

  return monthlyIncome * 12 < monthlyRent * 40;
}

export function GuarantorSection({ applicationId }: { applicationId: string }) {
  const [isAdding, setIsAdding] = useState(false);
  const currentIncomes = useWatch({ name: 'currentIncomes' });
  const { listing } = useApplicationContext();
  const showIncomeWarning = isIncomeBelowRequirement(currentIncomes, listing.rentalListing?.monthlyPayment);
  const { data, isError, isFetching, isLoading, refetch } = useGuarantorInvite(applicationId);
  const guarantorInvite = data?.guarantorInvite;
  const guarantorCompany = data?.selectedGuarantorCompany;
  const listingGuarantorPolicy = data?.listingGuarantorPolicy;

  if (data && !listingGuarantorPolicy?.acceptsGuarantors && !guarantorInvite && !guarantorCompany) {
    return null;
  }

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', marginTop: 'default' }}>
      {showIncomeWarning ? (
        <Alert tone="warning">
          Your income may not meet this property’s requirement. Adding a guarantor can strengthen your application.
        </Alert>
      ) : null}
      {isLoading ? (
        <Box
          role="status"
          aria-label="Loading guarantor"
          css={{ display: 'flex', flexDirection: 'column', gap: 'tighter', padding: 'default' }}
        >
          <LoadingParagraph isLoading loadingWidth="40%">
            Loading guarantor
          </LoadingParagraph>
          <LoadingParagraph isLoading loadingWidth="60%">
            Loading guarantor email
          </LoadingParagraph>
        </Box>
      ) : guarantorInvite ? (
        <Guarantor applicationId={applicationId} guarantor={guarantorInvite} />
      ) : guarantorCompany ? (
        <GuarantorCompany applicationId={applicationId} guarantorCompany={guarantorCompany} />
      ) : isError ? (
        <Alert
          tone="critical"
          actionButton={
            <TextButton type="button" disabled={isFetching} onClick={() => void refetch()}>
              Try again
            </TextButton>
          }
        >
          We couldn’t load guarantor information.
        </Alert>
      ) : (
        <Button
          fluid
          type="button"
          tone="brand"
          emphasis="outlined"
          icon={<IconPlusOutline />}
          onClick={() => setIsAdding(true)}
        >
          Add guarantor
        </Button>
      )}
      {isAdding && listingGuarantorPolicy ? (
        <AddGuarantorModal
          applicationId={applicationId}
          listingGuarantorPolicy={listingGuarantorPolicy}
          onClose={() => setIsAdding(false)}
        />
      ) : null}
    </Box>
  );
}

function Guarantor({
  applicationId,
  guarantor,
}: {
  applicationId: string;
  guarantor: InviteGuarantorWebApiResponseDto;
}) {
  const { enqueueToast } = useToast();
  const removeGuarantor = useCappsRenterGuarantorInvitationsWebApiHandler_removeGuarantor({
    meta: {
      invalidates: [GUARANTOR_INVITE_TAG],
      context: { api: 'cappsRenterGuarantorInvitations', details: 'removeGuarantor' },
    },
  });

  async function onRemove() {
    try {
      await removeGuarantor.mutateAsync({ postData: { applicationId } });
    } catch (error) {
      logError({ error, context: 'Guarantor - removeGuarantor error' });
      enqueueToast(<Toast tone="critical">We couldn’t remove the guarantor. Try again.</Toast>);
    }
  }

  return (
    <Box
      role="group"
      aria-label={`Guarantor ${guarantor.firstName} ${guarantor.lastName}`}
      aria-busy={removeGuarantor.isPending}
      css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <GuarantorSummary guarantor={guarantor} />
      <TextButton type="button" disabled={removeGuarantor.isPending} onClick={onRemove}>
        Remove
      </TextButton>
    </Box>
  );
}

function GuarantorCompany({
  applicationId,
  guarantorCompany,
}: {
  applicationId: string;
  guarantorCompany: SelectedGuarantorCompanyWebApiDto;
}) {
  const { enqueueToast } = useToast();
  const saveGuarantorCompany = useSaveGuarantorCompany(applicationId, 'removeGuarantorCompany');

  async function onRemove() {
    try {
      await saveGuarantorCompany.save(undefined);
    } catch (error) {
      logError({ error, context: 'GuarantorCompany - removeGuarantorCompany error' });
      enqueueToast(<Toast tone="critical">We couldn’t remove the guarantor company. Try again.</Toast>);
    }
  }

  return (
    <Box
      role="group"
      aria-label={`Guarantor company ${guarantorCompany.displayName}`}
      aria-busy={saveGuarantorCompany.isPending}
      css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <GuarantorCompanySummary guarantorCompany={guarantorCompany} />
      <TextButton type="button" disabled={saveGuarantorCompany.isPending} onClick={onRemove}>
        Remove
      </TextButton>
    </Box>
  );
}

function AddGuarantorModal({
  applicationId,
  listingGuarantorPolicy,
  onClose,
}: {
  applicationId: string;
  listingGuarantorPolicy: ListingGuarantorPolicyWebApiDto;
  onClose: () => void;
}) {
  const acceptsPersonalGuarantor = listingGuarantorPolicy.acceptsPersonalGuarantor;
  const acceptsGuarantorCompany = listingGuarantorPolicy.acceptedCompanies.length > 0;
  const [guarantorType, setGuarantorType] = useState(
    acceptsPersonalGuarantor ? GuarantorType.PERSONAL : GuarantorType.COMPANY,
  );
  const [selectedCompany, setSelectedCompany] = useState<ThirdPartyGuarantorCompany | ''>('');
  const saveGuarantorCompany = useSaveGuarantorCompany(applicationId, 'saveGuarantorCompany');
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string }>({});
  const { application } = useApplicationContext();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const inviteGuarantor = useCappsRenterGuarantorInvitationsWebApiHandler_inviteGuarantor({
    meta: {
      invalidates: [GUARANTOR_INVITE_TAG],
      context: { api: 'cappsRenterGuarantorInvitations', details: 'inviteGuarantor' },
    },
  });

  async function onSendInvite() {
    const firstName = firstNameRef.current?.value.trim() ?? '';
    const lastName = lastNameRef.current?.value.trim() ?? '';
    const email = emailRef.current?.value.trim().toLowerCase() ?? '';
    const nextErrors = {
      firstName: firstName ? undefined : 'First name is required',
      lastName: lastName ? undefined : 'Last name is required',
      email: !email
        ? 'Email address is required'
        : !validate(email)
          ? 'Enter a valid email address'
          : email === String(application.answers?.email).toLowerCase()
            ? 'You cannot add yourself as a guarantor'
            : undefined,
    };

    if (nextErrors.firstName || nextErrors.lastName || nextErrors.email) {
      setErrors(nextErrors);
      return;
    }

    try {
      const invitedGuarantor = await inviteGuarantor.mutateAsync({
        postData: {
          applicationId,
          firstName,
          lastName,
          email,
        },
      });
      queryClient.setQueryData(
        CappsRenterGuarantorInvitationsWebApiHandler_getGuarantorInvite_queryOptions({ applicationId }).queryKey,
        (previous) => (previous ? { ...previous, guarantorInvite: invitedGuarantor } : previous),
      );
      onClose();
    } catch (error) {
      logError({ error, context: 'AddGuarantorModal - inviteGuarantor error' });
      enqueueToast(<Toast tone="critical">We couldn’t invite the guarantor. Try again.</Toast>);
    }
  }

  async function onSaveCompany() {
    const guarantorCompany = listingGuarantorPolicy.acceptedCompanies.find(
      ({ company }) => company === selectedCompany,
    );
    if (!guarantorCompany) {
      return;
    }

    try {
      await saveGuarantorCompany.save(guarantorCompany);
      onClose();
    } catch (error) {
      logError({ error, context: 'AddGuarantorModal - saveGuarantorCompany error' });
      enqueueToast(<Toast tone="critical">We couldn’t save the guarantor company. Try again.</Toast>);
    }
  }

  const isCompany = guarantorType === GuarantorType.COMPANY;
  const isPending = isCompany ? saveGuarantorCompany.isPending : inviteGuarantor.isPending;

  return (
    <Modal
      open
      size="sm"
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={2}>{isCompany ? 'Add guarantor company' : 'Invite guarantor'}</Heading>}
      body={
        <>
          {acceptsPersonalGuarantor && acceptsGuarantorCompany ? (
            <FormField
              label={<Label>Guarantor type</Label>}
              control={
                <Select
                  value={guarantorType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setGuarantorType(e.target.value as GuarantorType)
                  }
                >
                  <option value={GuarantorType.PERSONAL}>Personal guarantor</option>
                  <option value={GuarantorType.COMPANY}>Guarantor company</option>
                </Select>
              }
              css={{ marginBottom: 'default' }}
            />
          ) : null}
          {isCompany ? (
            <>
              <Alert css={{ marginBottom: 'default' }}>
                This only saves your selected company to your application. We won’t contact the company. Your landlord
                will contact you if anything else is needed.
              </Alert>
              <FormField
                label={<Label>Guarantor company</Label>}
                control={
                  <Select
                    value={selectedCompany}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setSelectedCompany(e.target.value as ThirdPartyGuarantorCompany)
                    }
                  >
                    <option value="" disabled>
                      Select a company
                    </option>
                    {listingGuarantorPolicy.acceptedCompanies.map(({ company, displayName }) => (
                      <option key={company} value={company}>
                        {displayName}
                      </option>
                    ))}
                  </Select>
                }
                css={{ marginBottom: 'default' }}
              />
              <TextButton asChild>
                <a href={APPLICATIONS_FAQ_URL} target="_blank" rel="noopener noreferrer">
                  Learn about guarantor companies
                </a>
              </TextButton>
            </>
          ) : (
            <>
              <Paragraph css={{ marginBottom: 'default' }}>
                When you select Save, we’ll email this person an invitation to complete their guarantor application.
                They’ll need to verify their identity and income.
              </Paragraph>
              {listingGuarantorPolicy.requireInState ? (
                <Alert css={{ marginBottom: 'default' }}>
                  Your guarantor must live in the same state as this property.
                </Alert>
              ) : null}
              <FormField
                label={<Label>First name</Label>}
                control={<Input ref={firstNameRef} maxLength={32} />}
                description={errors.firstName ? <FormHelp>{errors.firstName}</FormHelp> : undefined}
                error={!!errors.firstName}
                required
                css={{ marginBottom: 'default' }}
              />
              <FormField
                label={<Label>Last name</Label>}
                control={<Input ref={lastNameRef} maxLength={32} />}
                description={errors.lastName ? <FormHelp>{errors.lastName}</FormHelp> : undefined}
                error={!!errors.lastName}
                required
                css={{ marginBottom: 'default' }}
              />
              <FormField
                label={<Label>Email address</Label>}
                control={<Input ref={emailRef} maxLength={100} type="email" />}
                description={errors.email ? <FormHelp>{errors.email}</FormHelp> : undefined}
                error={!!errors.email}
                required
              />
            </>
          )}
        </>
      }
      footer={
        <Button
          type="button"
          tone="brand"
          emphasis="filled"
          disabled={isCompany ? !selectedCompany || isPending : isPending}
          loading={isPending}
          onClick={isCompany ? onSaveCompany : onSendInvite}
        >
          Save
        </Button>
      }
    />
  );
}
